import React from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { Alert } from '@mui/material';
import {
  BanknoteIcon,
  ChartNoAxesColumnIcon,
  EyeIcon,
  ImagesIcon,
  RadioTowerIcon,
  SettingsIcon,
  SpeechIcon,
  StarIcon,
  Trash2Icon,
} from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import {
  type DrawerTab,
  generateTab,
} from '@modules/_shared/components/drawer/DrawerTopBar';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { TeamContacts } from '@modules/teams/TeamContacts';
import { TeamImages } from '@modules/teams/TeamImages';
import { TeamOverview } from '@modules/teams/TeamOverview';
import { TeamPortals } from '@modules/teams/TeamPortals';
import { DELETE_TEAM, DRAWER_GET_TEAM } from '@modules/teams/teamQueries';
import { TeamReviews } from '@modules/teams/TeamReviews';
import { TeamSettings } from '@modules/teams/TeamSettings';
import { TeamTransactions } from '@modules/teams/TeamTransactions';
import { VisibilityDateRangeScope } from '@modules/visibility-dashboard/useVisibilityDateRange';
import { useLocale } from '@shared/hooks/locale';
import { fromArrayToStringHasura } from '@shared/utils/global-id';
import { lazyRetry } from '@shared/utils/lazyRetry';

const TeamStats = React.lazy(() =>
  lazyRetry(() => import('@modules/visibility-stats/VisibilityStats')),
);

export const TeamDrawer = () => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();
  const { teamId } = useParams() as { teamId: string };

  const currentTab =
    useMatch('/teams/:teamId/:tab/*')?.params.tab ?? 'overview';

  const { data, error } = useQuery(DRAWER_GET_TEAM, {
    variables: { id: teamId },
  });

  const userIds =
    data?.teams_by_pk?.teams_users_aggregate?.nodes?.map(
      elem => elem.user_id as string,
    ) ?? [];

  const [deleteTeam] = useMutation(DELETE_TEAM);

  const handleClose = () => {
    // Filter out team-specific scoped parameters when closing
    const cleanParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      if (!key.startsWith('team-')) {
        cleanParams.set(key, value);
      }
    }
    navigate({
      pathname: '../',
      search: cleanParams.toString() || '',
    });
  };

  const drawerActions = [
    {
      label: t('Delete team'),
      disabled: false,
      onClick: () => {
        if (window.confirm(t('Are you sure you want to delete this team?'))) {
          // Delete team and remove from cache
          deleteTeam({
            variables: {
              teamId,
              userIds,
              userIdsString: fromArrayToStringHasura(userIds ?? []),
            },
            update: cache => {
              cache.evict({ id: `teams:${teamId}` });
            },
          }).then(() => {
            // Close drawer
            handleClose();
          });
        }
      },
      icon: <Trash2Icon size={20} />,
    },
  ];

  const tabsData: [string, string, React.ReactElement?][] = [
    [t('Overview'), 'overview', <EyeIcon />],
    [t('Images'), 'images', <ImagesIcon />],
    [t('Settings'), 'settings', <SettingsIcon />],
    [t('Transactions'), 'transactions', <BanknoteIcon />],
    [t('Portals'), 'portals', <RadioTowerIcon />],
    [t('Stats'), 'stats', <ChartNoAxesColumnIcon />],
    [t('Contacts'), 'valuation-requests', <SpeechIcon />],
    [t('Google Reviews'), 'reviews', <StarIcon />],
  ];

  const tabs: DrawerTab[] = tabsData
    .filter(([, value]) => !['settings'].includes(value) || me?.is_admin)
    .map(([label, value, icon]) =>
      generateTab([searchParams.toString(), label, value, icon]),
    );

  return (
    <Drawer
      onClose={handleClose}
      title={`${t('Team')} / ${data?.teams_by_pk?.name}`}
      actions={drawerActions}
      currentTab={currentTab}
      tabs={tabs}
    >
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      ) : (
        <Routes>
          <Route
            index
            element={
              <Navigate
                replace
                to={{
                  pathname: './overview',
                  search: searchParams.toString(),
                }}
              />
            }
          />
          <Route path="overview/*" element={<TeamOverview />} />
          <Route path="images" element={<TeamImages />} />
          <Route path="settings" element={<TeamSettings />} />
          <Route path="transactions/*" element={<TeamTransactions />} />
          <Route path="portals/*" element={<TeamPortals />} />
          <Route
            path="stats/*"
            element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <VisibilityDateRangeScope>
                  <TeamStats initialTeamId={teamId} showTitle={false} />
                </VisibilityDateRangeScope>
              </React.Suspense>
            }
          />
          <Route path="valuation-requests/*" element={<TeamContacts />} />
          <Route path="reviews" element={<TeamReviews />} />
        </Routes>
      )}
    </Drawer>
  );
};
